import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, Briefcase, UserPlus, Zap } from 'lucide-react';

const engagementModels = [
  {
    title: 'Dedicated QA Team',
    description: 'A fully managed QA team working exclusively on your project.',
    badge: 'Most Popular',
    icon: Users
  },
  {
    title: 'Project-Based Testing',
    description: 'End-to-end testing for a specific release or project.',
    badge: null,
    icon: Briefcase
  },
  {
    title: 'Staff Augmentation',
    description: 'Experienced QA professionals integrated into your existing team.',
    badge: null,
    icon: UserPlus
  },
  {
    title: 'On-Demand Testing',
    description: 'Flexible testing support whenever you need it.',
    badge: 'Flexible',
    icon: Zap
  },
];

export const IndustriesEngagement = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#0f0f10] via-[#1a1a1a] to-[#0f0f10] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#8B1538] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Industries Section */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
          >
            04
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Industries We <span className="text-[#D4AF37]">Serve</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 md:p-12 rounded-sm border border-[#D4AF37]/20"
          >
            <p
              className="text-lg md:text-xl text-gray-300 leading-[1.8] font-light tracking-wide max-w-4xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              From <span className="italic text-[#D4AF37]">Banking &amp; Finance</span> to
              <span className="italic text-[#D4AF37]"> Healthcare</span>,
              <span className="italic text-[#D4AF37]"> Retail</span>,
              <span className="italic text-[#D4AF37]"> SaaS</span> and beyond — we deliver
              trusted quality assurance across a wide range of industries, tailoring our
              testing expertise to the unique demands of every sector we serve.
            </p>
          </motion.div>
        </div>

        {/* Engagement Models Section */}
        <div>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
          >
            05
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Engagement <span className="text-[#D4AF37]">Models</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl">
              Choose the engagement model that suits your business
            </p>
          </motion.div>

          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 md:p-12 rounded-sm border border-[#D4AF37]/20">
            <div className="grid md:grid-cols-2 gap-6">
              {engagementModels.map((model, index) => {
                const Icon = model.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="relative bg-[#0f0f10]/40 p-6 rounded-sm border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-all duration-300 group overflow-hidden"
                  >
                    {model.badge && (
                      <div className="absolute top-4 right-4 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B0] text-[#1a1a1a] text-[10px] font-bold px-2 py-0.5 rounded-sm">
                        {model.badge}
                      </div>
                    )}
                    
                    <div className="relative z-10 flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-[#1a1a1a]" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 pr-16">
                        <h3 className="text-lg md:text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors duration-300">
                          {model.title}
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed">
                          {model.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};